/**
 * Email Validation Utility with Typo Detection
 *
 * Validates email addresses and detects common typos in domains/TLDs.
 * Provides suggestions for corrections.
 */

// Common email domains for suggestion
const COMMON_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'mail.com',
  'protonmail.com',
  'live.com',
  'msn.com',
  'comcast.net',
  'att.net',
  'verizon.net',
  'cox.net',
  'sbcglobal.net'
];

// Common TLD typos and their corrections
const TLD_TYPOS = {
  'con': 'com',
  'cpm': 'com',
  'cm': 'com',
  'om': 'com',
  'comm': 'com',
  'coom': 'com',
  'xom': 'com',
  'vom': 'com',
  'ckm': 'com',
  'clm': 'com',
  'ner': 'net',
  'ney': 'net',
  'nrt': 'net',
  'met': 'net',
  'orh': 'org',
  'orf': 'org',
  'ogr': 'org',
  'edj': 'edu',
  'eud': 'edu',
  'edi': 'edu'
};

// Domain name typos (without TLD)
const DOMAIN_TYPOS = {
  'gmial': 'gmail',
  'gmal': 'gmail',
  'gamil': 'gmail',
  'gnail': 'gmail',
  'gmaill': 'gmail',
  'gmil': 'gmail',
  'gmai': 'gmail',
  'yahooo': 'yahoo',
  'yaho': 'yahoo',
  'yaoo': 'yahoo',
  'yahop': 'yahoo',
  'hotmal': 'hotmail',
  'hotmai': 'hotmail',
  'hotmial': 'hotmail',
  'hotamil': 'hotmail',
  'hotmails': 'hotmail',
  'outloo': 'outlook',
  'outlok': 'outlook',
  'outllook': 'outlook',
  'outlookl': 'outlook',
  'iclous': 'icloud',
  'icoud': 'icloud',
  'iclouds': 'icloud'
};

// Valid TLDs (common ones) - used for validation
// Commented out for now as it's not currently used in validation logic
// const VALID_TLDS = [
//   'com', 'net', 'org', 'edu', 'gov', 'mil', 'io', 'co', 'us', 'uk', 'ca',
//   'au', 'de', 'fr', 'es', 'it', 'jp', 'cn', 'in', 'br', 'mx', 'ru',
//   'info', 'biz', 'name', 'mobi', 'pro', 'app', 'dev', 'tech', 'online',
//   'store', 'shop', 'site', 'xyz', 'club', 'email'
// ];

/**
 * Validates an email address and returns validation result with suggestions
 * @param {string} email - The email address to validate
 * @returns {object} - { isValid, error, suggestion, correctedEmail }
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      error: 'Please enter an email address',
      suggestion: null,
      correctedEmail: null
    };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // Check for @ symbol
  if (!trimmedEmail.includes('@')) {
    return {
      isValid: false,
      error: 'Email must contain @ symbol',
      suggestion: null,
      correctedEmail: null
    };
  }

  const [localPart, domain] = trimmedEmail.split('@');

  // Validate local part (before @)
  if (!localPart || localPart.length === 0) {
    return {
      isValid: false,
      error: 'Please enter the part before @',
      suggestion: null,
      correctedEmail: null
    };
  }

  // Check for domain
  if (!domain || domain.length === 0) {
    return {
      isValid: false,
      error: 'Please enter the domain after @',
      suggestion: null,
      correctedEmail: null
    };
  }

  // Check if domain has a dot (TLD required)
  if (!domain.includes('.')) {
    // Check if it's a known domain without TLD
    const suggestion = findDomainSuggestion(domain);
    if (suggestion) {
      return {
        isValid: false,
        error: `Did you mean @${suggestion}?`,
        suggestion: `@${suggestion}`,
        correctedEmail: `${localPart}@${suggestion}`
      };
    }
    return {
      isValid: false,
      error: 'Please include the full domain (e.g., @gmail.com)',
      suggestion: null,
      correctedEmail: null
    };
  }

  // Split domain into name and TLD
  const domainParts = domain.split('.');
  const tld = domainParts[domainParts.length - 1];
  const domainName = domainParts.slice(0, -1).join('.');

  // Check for TLD typos
  if (TLD_TYPOS[tld]) {
    const correctedTld = TLD_TYPOS[tld];
    const correctedDomain = `${domainName}.${correctedTld}`;
    return {
      isValid: false,
      error: `Did you mean .${correctedTld}?`,
      suggestion: `.${correctedTld}`,
      correctedEmail: `${localPart}@${correctedDomain}`
    };
  }

  // Check for domain name typos
  if (DOMAIN_TYPOS[domainName]) {
    const correctedDomainName = DOMAIN_TYPOS[domainName];
    const correctedDomain = `${correctedDomainName}.${tld}`;
    return {
      isValid: false,
      error: `Did you mean @${correctedDomain}?`,
      suggestion: `@${correctedDomain}`,
      correctedEmail: `${localPart}@${correctedDomain}`
    };
  }

  // Check if TLD is valid (at least 2 characters)
  if (tld.length < 2) {
    return {
      isValid: false,
      error: 'Invalid domain extension',
      suggestion: null,
      correctedEmail: null
    };
  }

  // Check for very short TLDs that are likely typos
  if (tld.length === 2 && !isValidCountryCode(tld)) {
    // Could be a typo like ".co" for ".com"
    if (tld === 'co' && domainName.match(/(gmail|yahoo|hotmail|outlook)/)) {
      return {
        isValid: false,
        error: `Did you mean @${domainName}.com?`,
        suggestion: '.com',
        correctedEmail: `${localPart}@${domainName}.com`
      };
    }
  }

  // Basic format validation with regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Please enter a valid email format',
      suggestion: null,
      correctedEmail: null
    };
  }

  // All checks passed
  return {
    isValid: true,
    error: null,
    suggestion: null,
    correctedEmail: null
  };
}

/**
 * Find a domain suggestion for a partial domain (without TLD)
 */
function findDomainSuggestion(partialDomain) {
  const lower = partialDomain.toLowerCase();

  // Direct match with common domain names
  const domainMatches = {
    'gmail': 'gmail.com',
    'yahoo': 'yahoo.com',
    'hotmail': 'hotmail.com',
    'outlook': 'outlook.com',
    'icloud': 'icloud.com',
    'aol': 'aol.com',
    'mail': 'mail.com',
    'protonmail': 'protonmail.com',
    'live': 'live.com',
    'msn': 'msn.com'
  };

  if (domainMatches[lower]) {
    return domainMatches[lower];
  }

  // Check for typos in domain name
  if (DOMAIN_TYPOS[lower]) {
    const correctedDomain = DOMAIN_TYPOS[lower];
    return `${correctedDomain}.com`;
  }

  // Find closest match
  for (const domain of COMMON_DOMAINS) {
    const domainWithoutTld = domain.split('.')[0];
    if (lower === domainWithoutTld || lower.includes(domainWithoutTld) || domainWithoutTld.includes(lower)) {
      return domain;
    }
  }

  return null;
}

/**
 * Check if a 2-letter code is a valid country TLD
 */
function isValidCountryCode(code) {
  const countryCodes = [
    'us', 'uk', 'ca', 'au', 'de', 'fr', 'es', 'it', 'jp', 'cn', 'in', 'br',
    'mx', 'ru', 'nl', 'be', 'ch', 'at', 'se', 'no', 'dk', 'fi', 'pl', 'cz',
    'ie', 'nz', 'sg', 'hk', 'kr', 'tw', 'th', 'my', 'ph', 'id', 'vn', 'za',
    'ar', 'cl', 'co', 'pe', 've', 'ec', 'uy', 'pt', 'gr', 'tr', 'il', 'ae',
    'sa', 'eg', 'ke', 'ng', 'io', 'me', 'tv', 'cc', 'ws', 'to', 'fm', 'ai'
  ];
  return countryCodes.includes(code.toLowerCase());
}

/**
 * Format email by applying suggestion/correction
 */
export function applyEmailCorrection(email, correctedEmail) {
  if (correctedEmail) {
    return correctedEmail;
  }
  return email;
}

/**
 * Get validation message type for styling
 * @returns {'error' | 'warning' | 'suggestion' | null}
 */
export function getValidationMessageType(validationResult) {
  if (validationResult.isValid) return null;
  if (validationResult.suggestion) return 'suggestion';
  return 'error';
}

export default validateEmail;
